import type { RawComponent } from '@/features/content/useComponent';

// The exact image URLs this chapter contains.
//
// MUST mirror the backend's chapterChatImageURL: prefer `image_url`, fall back to
// `dynamic_image_url`, and take the value verbatim. Those are the strings the
// backend injected into the model's context and verified assistant <img> sources
// against, so this is the set the renderer has to allow.
//
// Deliberately NOT the display `src` from the normalized ImageUnitData — that
// one carries a `=sN` resize suffix, so it would never match what the model was
// shown.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function getChapterImageUrls(
  component: RawComponent | null | undefined,
): ReadonlySet<string> {
  const urls = new Set<string>();

  if (!component) {
    return urls;
  }

  for (const section of toArray(component.sections)) {
    if (!isRecord(section)) {
      continue;
    }

    const contentSection = section.content_section ?? section.contentSection;

    if (!isRecord(contentSection)) {
      continue;
    }

    const units = contentSection.content_units ?? contentSection.contentUnits;

    for (const unit of toArray(units)) {
      if (!isRecord(unit)) {
        continue;
      }

      const url =
        asUrl(unit.image_url ?? unit.imageUrl) ??
        asUrl(unit.dynamic_image_url ?? unit.dynamicImageUrl);

      if (url) {
        urls.add(url);
      }
    }
  }

  return urls;
}
